/**
 * A Name or Asset Identifier edited in the cell (`equipment-page-table`, R7–R10; frame 07, pin 4).
 *
 * A page composition of the design system's text `Button` and compact `Input`:
 * - **at rest** the value is a text button sized to it (a 24 × 24 px minimum target). Pressing it
 *   swaps in the field, holding the value, all selected;
 * - **open**, the answer is worked out as it is typed and drawn under the field (the input's
 *   description): cyan for *Will be saved as …*, the error ink for a refusal;
 * - **Enter, Tab or leaving the field** commits; **Escape** cancels and puts the old value back.
 *   Either way focus goes back to the ROW, so the table's arrows and F2 keep working (criterion 15:
 *   *Escape cancels back to the row*). A refused answer — or a commit the server just refused — on
 *   blur cancels rather than commits, so leaving a field never writes what the line refused. Tab
 *   from the Name opens the same row's Asset Identifier; Shift+Tab goes back; Tab out of the last
 *   field returns to the row;
 * - **saving**, the value being SAVED (the answer's, not what was typed) shows on the button in its
 *   busy state (the button's own spinner), until the re-read lands;
 * - **a refused commit** reopens the field with what was typed and the server's line — unless the
 *   person has since opened another cell, which is left alone (the toast still says why).
 *
 * A click on the button or in the field is the cell's own, never the row's: the `Table` leaves a
 * click or key on a `button` or `input` inside a row alone.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Input } from '@repo/design-system';
import { fontMono, ink, semantic, space, typeScale } from '@repo/design-tokens';

import type { EditableRegisterField } from './equipmentRegisterColumns';
import type { CellWriteOutcome } from './useEquipmentRowWrites';

/** What the typed text would be saved as, from `equipmentRowEdit`. */
export interface CellAnswer {
  /** The line under the field; null draws none. */
  line: string | null;
  /** A refusal: Enter is refused and blur cancels. */
  refused: boolean;
  /** Nothing to send: Enter and blur close the field. */
  unchanged: boolean;
  /** What a commit would save — the answer's name, or the identifier (`null` clears it). */
  saves: string | null;
}

/** What a closed cell answers: nothing to say, nothing to send. Never read while closed. */
const CLOSED_ANSWER: CellAnswer = { line: null, refused: false, unchanged: true, saves: null };

export interface EditableRegisterCellProps {
  field: EditableRegisterField;
  /** The value shown at rest; `null` (a blank identifier) shows *—*. */
  value: string | null;
  /**
   * The line beneath the value at rest — what the customer wrote, or the source id. While the
   * field is open the answer line takes its place, so the row keeps frame 07's height.
   */
  sub?: string | null;
  /** The row's display name, for the controls' names (A3). */
  rowName: string;
  /** For the answer line's id. */
  rowId: number;
  open: boolean;
  onOpen: () => void;
  /** After a refused commit: reopen this cell, only if no other cell has been opened since. */
  onReopen: () => void;
  onClose: () => void;
  /** Tab / Shift+Tab out of the field, after it commits. */
  onTabOut?: (backwards: boolean) => void;
  answerFor: (typed: string) => CellAnswer;
  /** Sends the commit; resolves with the line to reopen on, or ok. */
  commit: (typed: string) => Promise<CellWriteOutcome>;
}

const Shell = styled.span`
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;

  &[data-field='identifier'] .cell-value,
  &[data-field='identifier'] input,
  &[data-field='identifier'] .cell-sub {
    font-family: ${fontMono};
  }
  .cell-sub {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${ink[3]};
    font-size: ${typeScale.captionCompact.fontSize};
    line-height: ${typeScale.captionCompact.lineHeight};
  }
  .cell-value {
    min-height: ${space[6]};
    min-width: ${space[6]};
    justify-content: flex-start;
    max-width: 100%;
  }
  .cell-value .tl-btn-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/**
 * Frame 07's note under a field: cyan for the saved-as answer, the error ink for a refusal. One
 * line, clipped as frame 07 clips it, so a row keeps its height; the whole sentence is the field's
 * description, so a screen reader hears all of it. Shared with the add row, which reads "as in a
 * cell" (R17).
 */
export const AnswerLine = styled.span<{ $refused: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${typeScale.captionCompact.fontSize};
  line-height: ${typeScale.captionCompact.lineHeight};
  color: ${({ $refused }) =>
    $refused ? semantic.feedback.errorStrong : semantic.interactive.press};
`;

export const EditableRegisterCell: React.FC<EditableRegisterCellProps> = ({
  field,
  value,
  sub = null,
  rowName,
  rowId,
  open,
  onOpen,
  onReopen,
  onClose,
  onTabOut,
  answerFor,
  commit,
}) => {
  const [typed, setTyped] = useState(value ?? '');
  // The value a commit is saving, boxed so a cleared identifier (`null`) is still "saving".
  const [saving, setSaving] = useState<{ value: string | null } | null>(null);
  // A refused commit's line (a server 400, a failed holder rename): shown until the next keystroke.
  const [failure, setFailure] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Set while a key has already settled the field, so the blur that follows does not settle it again.
  const settled = useRef(false);
  // The fallback that ends a saving state the re-read never matched; one at a time, gone on unmount.
  const savingTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(savingTimer.current), []);

  // Opening starts from the value on screen, all selected.
  useLayoutEffect(() => {
    if (!open) return;
    settled.current = false;
    setTyped(value ?? '');
    setFailure(null);
    const input = inputRef.current;
    input?.focus();
    input?.select();
    // Only on opening: a re-read while typing must not throw the typing away.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Saving ends when the re-read shows the row's new value (or a failure reopened the field).
  useEffect(() => {
    if (saving !== null && (value ?? '') === (saving.value ?? '')) setSaving(null);
  }, [value, saving]);

  // Worked out only while open: a closed Name cell has no name index built, and building one per
  // closed cell on every render was a whole-twin build per row (Copilot, PR #2157).
  const answer = open ? answerFor(typed) : CLOSED_ANSWER;
  const line = failure ?? answer.line;
  const refused = failure !== null || answer.refused;
  const answerId = `${field}-answer-${rowId}`;

  /**
   * Back to the row (criterion 15), found while the field is still mounted. A focused row is the
   * table's one Tab stop, so its arrows, F2 and Enter work at once.
   */
  const backToRow = () => {
    const row = (inputRef.current ?? buttonRef.current)?.closest<HTMLElement>('[role="row"]');
    window.setTimeout(() => {
      // Only where focus was lost with the field. A refused commit can reopen the field before
      // this runs, and taking focus from it would blur it — and blur commits.
      const active = document.activeElement;
      if (active && active !== document.body) return;
      // A row the table can focus (the Rows view's); the Groups view's rows take no focus, so
      // there it is this cell's own button, drawn again now the field has closed.
      if (row?.hasAttribute('tabindex')) row.focus();
      else row?.querySelector<HTMLElement>(`[data-edit-field="${field}"]`)?.focus();
    }, 0);
  };

  const cancel = (refocus: boolean) => {
    settled.current = true;
    setTyped(value ?? '');
    setFailure(null);
    if (refocus) backToRow();
    onClose();
  };

  /** Commit what is typed. Resolves false when the field must stay open (refused). */
  const settle = async (via: 'enter' | 'tab' | 'blur'): Promise<boolean> => {
    // On blur, a line the server just gave counts as a refusal too: leaving never re-sends it.
    if (answer.refused || (via === 'blur' && failure !== null)) {
      if (via === 'blur') cancel(false);
      return false;
    }
    settled.current = true;
    if (answer.unchanged) {
      if (via === 'enter') backToRow();
      onClose();
      return true;
    }
    const sent = typed;
    window.clearTimeout(savingTimer.current);
    setSaving({ value: answer.saves });
    if (via === 'enter') backToRow();
    onClose();
    const outcome = await commit(sent);
    if (!outcome.ok) {
      setSaving(null);
      onReopen();
      // After the reopen's own reset: keep what was typed, and say why it was not saved.
      window.setTimeout(() => {
        setTyped(sent);
        setFailure(outcome.line);
      }, 0);
      return false;
    }
    // A value the server stores differently (or a re-read that fails) would otherwise spin on.
    savingTimer.current = window.setTimeout(() => setSaving(null), 15_000);
    return true;
  };

  if (open) {
    return (
      <Shell data-field={field}>
        <Input
          ref={inputRef}
          size="compact"
          onGlass
          value={typed}
          invalid={refused}
          aria-label={field === 'name' ? `Name of ${rowName}` : `Asset Identifier of ${rowName}`}
          aria-describedby={line ? answerId : undefined}
          data-edit-input={field}
          onChange={event => {
            setTyped(event.target.value);
            setFailure(null);
          }}
          onKeyDown={event => {
            // The cell's keys are its own: never the row's arrows, Space or Enter.
            event.stopPropagation();
            if (event.key === 'Enter') {
              event.preventDefault();
              void settle('enter');
            } else if (event.key === 'Escape') {
              event.preventDefault();
              cancel(true);
            } else if (event.key === 'Tab') {
              event.preventDefault();
              // A refused answer keeps the field; otherwise the commit starts and the next field
              // opens at once, without waiting for the write to land.
              if (answer.refused) return;
              const backwards = event.shiftKey;
              // Name → Identifier, or back; out of the last field, back to the row. With the identify
              // card docked the Groups view draws no Asset Identifier, so there is no next field.
              const neighbour = field === 'name' ? 'identifier' : 'name';
              const opensNext =
                (field === 'name' ? !backwards : backwards) &&
                Boolean(
                  event.currentTarget
                    .closest('[role="row"]')
                    // Not a neighbour still saving: opening it would start a second write.
                    ?.querySelector(`[data-edit-field="${neighbour}"]:not([aria-busy="true"])`),
                );
              if (!opensNext) backToRow();
              void settle('tab');
              if (opensNext) onTabOut?.(backwards);
            }
          }}
          onBlur={() => {
            if (settled.current) return;
            void settle('blur');
          }}
        />
        {line ? (
          <AnswerLine id={answerId} $refused={refused} title={line}>
            {line}
          </AnswerLine>
        ) : (
          sub && <span className="cell-sub">{sub}</span>
        )}
      </Shell>
    );
  }

  const shown = saving ? saving.value : value;
  return (
    <Shell data-field={field}>
      <Button
        ref={buttonRef}
        type="button"
        variant="text"
        size="xs"
        className="cell-value"
        loading={saving !== null}
        aria-label={
          field === 'name' ? `Rename ${rowName}` : `Edit the Asset Identifier of ${rowName}`
        }
        data-edit-field={field}
        // F2 on the row opens the Name: said here, where a screen reader reaches the cell.
        aria-keyshortcuts={field === 'name' ? 'F2' : undefined}
        // Not a Tab stop: the table's body is ONE stop, moved through with the arrows (criterion
        // 15). The keyboard reaches the field with F2 on the row; a pointer clicks the value.
        tabIndex={-1}
        onClick={onOpen}
      >
        {shown || '—'}
      </Button>
      {sub && <span className="cell-sub">{sub}</span>}
    </Shell>
  );
};

export default EditableRegisterCell;
